import React from "react";
import { useTranslation } from "react-i18next";
import { usePopup } from "@/context/PopupContext";
import { getSiteConfig } from "@/config/siteConfigs";
import CardSlider from "../Slider/Slider";
import HeaderSliderCard from "../HeaderSlider/HeaderSliderCard/HeaderSliderCard";
import styles from "./HeaderSlider.module.scss";

interface SliderImage {
  src1920: string;
  src1440: string;
  src768: string;
  src320: string;
  alt: string;
  imageType?: "full" | "partial";
  className?: string;
  title?: string;
  description?: string;
  isH1?: boolean;
}

interface HeaderSliderProps {
  images: SliderImage[];
}

const HeaderSlider: React.FC<HeaderSliderProps> = ({ images }) => {
  const { t } = useTranslation("common");
  const { openMenu } = usePopup();
  const siteConfig = getSiteConfig();

  const whatsappPhone = siteConfig.contact.whatsappPhone;
  const whatsappLink = siteConfig.social.whatsapp;

  const sliderSettings = {
    slidesPerView: 1,
    spaceBetween: 0,
    loop: true,
    navigation: {
      prevEl: `.${styles.customPrev}`,
      nextEl: `.${styles.customNext}`,
    },
    pagination: {
      clickable: true,
      el: `.${styles.swiperPagination}`,
    },
  };

  return (
    <div className={styles.sliderWrapper}>
      {/* WhatsApp phone — mobile only */}
      {whatsappPhone && whatsappLink !== "#" && (
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.whatsappBanner}
        >
          <span>{whatsappPhone}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="#25d366"
            aria-hidden="true"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </a>
      )}

      <CardSlider settings={sliderSettings}>
        {images.map((image) => (
          <HeaderSliderCard
            key={image.src1920}
            imageSrc1920={image.src1920}
            imageSrc1440={image.src1440}
            imageSrc768={image.src768}
            imageSrc320={image.src320}
            alt={image.alt}
            imageType={image.imageType || "full"}
            className={image.className}
            title={image.title}
            isH1={image.isH1}
          />
        ))}
      </CardSlider>

      <div className={styles.bottomRow}>
        <p className={styles.promoLine}>{t("headerSlider.promoLine")}</p>
        <button
          className={`commonButton buttonLg ${styles.button}`}
          onClick={openMenu}
        >
          {t("headerSlider.button")}
        </button>
      </div>

      <div className={styles.navigationWrapper}>
        <button
          className={styles.customPrev}
          aria-label={t("common.previous") || "Previous slide"}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            fill="none"
            aria-hidden="true"
          >
            <path
              stroke="#fff"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13.31 4 3 14l10.31 10M8.552 14H25"
            />
          </svg>
        </button>

        <div className={styles.swiperPagination}></div>

        <button
          className={styles.customNext}
          aria-label={t("common.next") || "Next slide"}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            fill="none"
            aria-hidden="true"
          >
            <path
              stroke="#fff"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M14.69 4 25 14 14.69 24m4.758-10H3"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default HeaderSlider;
